import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { NativeRead } from '../api/envelope'
import { formatDate, formatDateTime, formatMoney } from './format'
import type { FreshnessVm, PriceListIntent } from './types'

/** Строк на странице прайса: как у каталога кабинета. */
export const PRICE_LIST_PAGE_SIZE = 50
export const PRICE_LIST_MAX_SEARCH = 128

/** Прайс базы по виду цен (RP-FB10) — ответ `catalog/price-list` после декодера. */
export interface PriceList {
  slice: { observedAt: string; freshness: 'fresh' | 'stale'; staleReason: string | null; refreshing: boolean }
  priceTypes: readonly { key: string; name: string | null }[]
  /** Выбранный вид; `null` — в срезе нет ни одной цены. */
  priceType: string | null
  total: number
  page: number
  pageSize: number
  items: readonly {
    productKey: string
    name: string | null
    sku: string | null
    price: number
    period: string
    priceTypeKey: string
    priceTypeName: string | null
  }[]
}

/** Все виды цен одним списком — вид по умолчанию: цены как в 1С, а не прайс одного вида. */
export const ALL_PRICE_TYPES = 'all'

export const EMPTY_PRICE_LIST_INTENT: PriceListIntent = { priceType: null, search: '', query: '', page: 0 }

export interface PriceListArgs {
  resourceId: string
  priceType: string | null
  query: string
  page: number
  pageSize: number
}

export const priceListArgs = (resourceId: string, intent: PriceListIntent): PriceListArgs => ({
  resourceId,
  priceType: intent.priceType ?? ALL_PRICE_TYPES,
  // Поиска в списке цен нет: список — записи регистра, а не номенклатура; запомненный прежней вкладкой поиск не применяется.
  query: '',
  page: intent.page,
  pageSize: PRICE_LIST_PAGE_SIZE,
})

export interface PriceListRowVm {
  /** Запись списка: товар × вид цен. */
  key: string
  productKey: string
  priceTypeKey: string
  /** Вид цен записи словами: «Розничная», «Закупочная». */
  priceTypeName: string
  /** Дата записи как пришла (начало суток): по ней запись находится для удаления. */
  period: string
  name: string
  sku: string
  price: string
  date: string
}

export interface PriceListVm {
  query: QueryView
  freshness: FreshnessVm | null
  /** Варианты вида цен по имени; пусто — среза ещё нет или цен нет. */
  priceTypes: readonly { value: string; label: string }[]
  priceType: string
  rows: readonly PriceListRowVm[]
  page: number
  pageCount: number
  total: number
}

export interface PriceListActions {
  priceTypeChosen: (key: string) => void
  setPage: (page: number) => void
  reload: () => void
}

const EMPTY_ROWS: readonly PriceListRowVm[] = []
const EMPTY_TYPES: readonly { value: string; label: string }[] = []

function sliceFreshness(slice: PriceList['slice']): FreshnessVm {
  const when = `Срез цен 1С на ${formatDateTime(slice.observedAt)}`
  if (slice.freshness === 'fresh')
    return { kind: 'fresh', label: 'Актуально', detail: slice.refreshing ? `${when}, срез обновляется` : when }
  return {
    kind: 'stale',
    label: 'Устарело',
    detail: [when, slice.staleReason === null ? null : `Причина: ${slice.staleReason}`].filter((part) => part !== null).join('. '),
  }
}

/** Именованный селектор окна прайса: вход — ответ endpoint-а и намерение, а не RootState. */
export const makeSelectPriceListVm = () =>
  createSelector(
    [(input: PriceListInput) => input.list, (input: PriceListInput) => input.intent],
    (list, intent): PriceListVm => {
      const query = queryView(list, (read) => read.data.items.length === 0)
      const data = list.data?.data
      const ready = query.status === 'ready' && data !== undefined
      return {
        query,
        freshness: data === undefined ? null : sliceFreshness(data.slice),
        priceTypes:
          data === undefined || data.priceTypes.length === 0
            ? EMPTY_TYPES
            : [
                { value: ALL_PRICE_TYPES, label: 'Все виды цен' },
                ...data.priceTypes.map((type) => ({ value: type.key, label: type.name ?? `Вид цен без имени (${type.key})` })),
              ],
        priceType: data?.priceType ?? intent.priceType ?? ALL_PRICE_TYPES,
        rows:
          !ready || data.items.length === 0
            ? EMPTY_ROWS
            : data.items.map((item) => ({
                key: `${item.productKey}:${item.priceTypeKey}`,
                productKey: item.productKey,
                priceTypeKey: item.priceTypeKey,
                priceTypeName: item.priceTypeName ?? 'Вид цен без имени',
                period: item.period,
                name: item.name ?? 'Наименование не прочитано из 1С',
                sku: item.sku ?? '—',
                price: formatMoney(item.price),
                date: formatDate(item.period),
              })),
        page: intent.page,
        pageCount: Math.max(1, Math.ceil((data?.total ?? 0) / PRICE_LIST_PAGE_SIZE)),
        total: data?.total ?? 0,
      }
    },
  )

export interface PriceListInput {
  list: QueryResultLike<NativeRead<PriceList>>
  intent: PriceListIntent
}
